/**
 * BatchStatusCell — one batch's status as the server words it (EXR-W1 W1-10).
 * =================================================================================
 * `status_word` + `status_tone` through StatusIndicator; QA's `disposition` beside it as a separate word
 * ("Completed · Released by QA Manager 2, 06 Oct"); the gate facts as a compact BatchStatusBanner.
 * The client derives nothing: a row without `status_word` shows a dash.
 */
import React from 'react';
import StatusIndicator from '@/components/genealogy/StatusIndicator';
import BatchStatusBanner from '@/components/batch/BatchStatusBanner';
import { formatDateTime } from '@/utils/datetime';

/** "Released by QA Manager 2, 06 Oct" — the disposition word with who and when, as the server gave them. */
export const dispositionText = (d) => {
  if (!d || !d.word) return '';
  const who = d.by ? ` by ${d.by}` : '';
  const when = d.at ? `, ${formatDateTime(d.at, 'DD MMM')}` : '';
  return `${d.word}${who}${when}`;
};

/** `detail` false: the word only, with who and when in the tooltip (a table that shows them in their own columns). */
export const DispositionIndicator = ({ disposition, detail = true }) =>
  disposition && disposition.word ? (
    <StatusIndicator
      state={disposition.tone}
      label={detail ? dispositionText(disposition) : disposition.word}
      tooltip={detail ? (disposition.at ? formatDateTime(disposition.at) : undefined) : dispositionText(disposition)}
    />
  ) : null;

/** `showDisposition` false: the batch's own status alone (a list that states the release in its own columns). */
const BatchStatusCell = ({ record, showBanner = true, dispositionDetail = true, showDisposition = true }) => {
  if (!record || !record.status_word) return <span className="text-gray-400">—</span>;
  return (
    <div data-testid="batch-status-cell" style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <StatusIndicator state={record.status_tone} label={record.status_word} />
        {showDisposition && record.disposition && record.disposition.word ? (
          <>
            <span aria-hidden style={{ marginInlineEnd: 10 }}>
              ·
            </span>
            <DispositionIndicator disposition={record.disposition} detail={dispositionDetail} />
          </>
        ) : null}
      </span>
      {showBanner ? <BatchStatusBanner gate={record.gate} statusWord={record.status_word} compact /> : null}
    </div>
  );
};

export default BatchStatusCell;
